import { Article } from "../api/types";
import { unsplashImageUrl } from "../utils/unsplashImage";

const ARTICLE_IMAGE_PHOTOS: Record<string, string> = {
    "journal-slow-living": "photo-1661242686756-de9bdeb69ad8",
    "journal-natural-materials": "photo-1758915753451-b478b4e6f920",
    "journal-styling-ceramics": "photo-1628744876497-eb30460be9f6",
    "journal-lighting-guide": "photo-1631679706909-1844bbd07221",
    "journal-small-space": "photo-1724582586458-a51791349977",
    "journal-caring-linen-wool": "photo-1649083048770-82e8ffd80431",
};

interface ArticleInput {
    id: number;
    handle: string;
    title: string;
    excerpt: string;
    paragraphs: string[];
    category: string;
    author: string;
    publishedAt: string;
    imageSeed: string;
    relatedProductHandles: string[];
    readingMinutes: number;
}

const blog = { handle: "journal", title: "Journal" };

const articleInputs: ArticleInput[] = [
    {
        id: 1,
        handle: "the-art-of-slow-living",
        title: "The Art of Slow Living",
        excerpt: "A home doesn't need more objects — it needs fewer, better ones. On choosing pieces that earn their place.",
        paragraphs: [
            "Slow living isn't about owning less for its own sake. It's about surrounding yourself only with objects you've actually chosen — pieces with a material, a maker, a reason for being there.",
            "A linen throw that softens with every wash. A bowl thrown by hand, imperfect in exactly the way that makes it interesting. These are things you notice again after years of living with them, not just on the day they arrive.",
            "The alternative — filling a room quickly, cheaply, and often — produces the opposite effect: a space that never quite settles, because nothing in it was chosen carefully enough to stay.",
            "Start small. Replace one thing you don't love with one thing you do. Let the room fill in slowly, on its own time.",
        ],
        category: "Living",
        author: "Maison Noma Journal",
        publishedAt: "2026-07-18",
        imageSeed: "journal-slow-living",
        relatedProductHandles: ["linen-throw", "boucle-cushion", "handwoven-wool-rug"],
        readingMinutes: 5,
    },
    {
        id: 2,
        handle: "a-guide-to-natural-materials",
        title: "A Guide to Natural Materials",
        excerpt: "Oak, linen, stoneware, brass — why the material always matters more than the finish.",
        paragraphs: [
            "A well-chosen material does two things a synthetic one rarely can: it ages with intention, and it tells you something true about what you're looking at.",
            "Solid oak darkens slightly with light exposure over the years. Brass develops a soft patina where it's touched most often. Linen grows softer with every wash. None of this is a flaw — it's the material recording time.",
            "When evaluating a piece, ask what it's actually made of, not just what it looks like in a photograph. Veneer over composite behaves nothing like solid wood; a glaze over earthenware behaves nothing like stoneware fired at a higher temperature.",
            "Buying fewer things, made from materials that improve rather than degrade, is quietly one of the most sustainable choices you can make in how you furnish a home.",
        ],
        category: "Materials",
        author: "Maison Noma Journal",
        publishedAt: "2026-06-22",
        imageSeed: "journal-natural-materials",
        relatedProductHandles: ["solid-oak-stool", "arco-side-table", "terra-vase"],
        readingMinutes: 6,
    },
    {
        id: 3,
        handle: "styling-ceramics-on-open-shelving",
        title: "Styling Ceramics on Open Shelving",
        excerpt: "Open shelves are unforgiving — every object is on display. Here's how to make that work in your favour.",
        paragraphs: [
            "The instinct with open shelving is to fill every gap. Resist it. Negative space is what lets a shelf read as curated rather than cluttered.",
            "Group in odd numbers, and let height do the work: a taller vase anchors a cluster of smaller bowls far better than three same-sized objects lined up in a row.",
            "Vary the finish, not just the shape. A matte stoneware bowl next to a glossy glazed vase creates the kind of quiet contrast that keeps a shelf visually interesting without any single piece shouting for attention.",
            "Finally, edit seasonally. What feels right in a bright summer room can feel thin in winter — swapping two or three pieces is often enough to reset the whole composition.",
        ],
        category: "Styling",
        author: "Maison Noma Journal",
        publishedAt: "2026-05-30",
        imageSeed: "journal-styling-ceramics",
        relatedProductHandles: ["nordic-bowl-set", "forma-vase", "wall-shelf-trio"],
        readingMinutes: 4,
    },
    {
        id: 4,
        handle: "choosing-lighting-that-feels-like-home",
        title: "Choosing Lighting That Feels Like Home",
        excerpt: "Most rooms have one light source and too much of it. Layered lighting changes everything.",
        paragraphs: [
            "A single overhead fixture flattens a room. The fix isn't a brighter bulb — it's more sources, each doing less work.",
            "Start with a floor lamp in the room's darkest corner, then add a table lamp somewhere you actually sit and read. Reserve pendant lighting for where you gather — over a table, above an island — rather than as general illumination.",
            "Warm-toned bulbs, roughly 2700K, do more to make a space feel considered than any fixture alone. It's a small, inexpensive change with an outsized effect.",
            "Dimmers matter more than most people expect. The same lamp can serve a working afternoon and a quiet evening — it just needs the range to do both.",
        ],
        category: "Lighting",
        author: "Maison Noma Journal",
        publishedAt: "2026-04-14",
        imageSeed: "journal-lighting-guide",
        relatedProductHandles: ["noma-table-lamp", "curve-floor-lamp", "orb-pendant-light"],
        readingMinutes: 5,
    },
    {
        id: 5,
        handle: "small-space-timeless-design",
        title: "Small Space, Timeless Design",
        excerpt: "Designing for a smaller footprint doesn't mean designing for less — it means designing with more precision.",
        paragraphs: [
            "In a small room, every object has to justify its presence twice: once for what it does, and once for how it looks doing it. That constraint, handled well, produces better rooms than unlimited space ever does.",
            "Favour furniture that's genuinely multi-purpose over furniture that merely looks compact. A side table with real presence often does more for a small room than three smaller, forgettable pieces.",
            "Vertical space is underused in most small rooms. A modular shelving system draws the eye upward and adds storage without eating into floor space — one of the few upgrades with no real trade-off.",
            "Resist the urge to match everything. A room built entirely from one collection can start to feel like a showroom rather than a home.",
        ],
        category: "Design",
        author: "Maison Noma Journal",
        publishedAt: "2026-03-08",
        imageSeed: "journal-small-space",
        relatedProductHandles: ["ellipse-side-table", "sculpted-stool", "modular-shelving-unit"],
        readingMinutes: 6,
    },
    {
        id: 6,
        handle: "caring-for-linen-and-wool",
        title: "Caring for Linen and Wool",
        excerpt: "Two of the most forgiving materials in the home — if you treat them the way they're meant to be treated.",
        paragraphs: [
            "Linen wants to be washed, not babied. Cool water, a gentle cycle, and air drying keep its texture intact and its fibres strong for years — far longer than more delicate fabrics.",
            "Wool is the opposite: it prefers to be left alone. Spot clean where possible, air out between uses, and reserve washing — cold water, wool-specific detergent — for when it's truly needed.",
            "Both materials benefit from a rest. Rotating a throw or a rug out of daily use occasionally lets the fibres recover their shape and lets you notice it again when it comes back.",
            "A light steam, rather than an iron, revives creased linen without flattening its natural texture — the slight irregularity is the point.",
        ],
        category: "Care",
        author: "Maison Noma Journal",
        publishedAt: "2026-02-19",
        imageSeed: "journal-caring-linen-wool",
        relatedProductHandles: ["linen-throw", "wool-blend-throw", "handwoven-wool-rug"],
        readingMinutes: 4,
    },
];

function buildArticle(input: ArticleInput): Article {
    return {
        id: `gid://shopify/Article/${input.id}`,
        handle: input.handle,
        title: input.title,
        excerpt: input.excerpt,
        contentHtml: input.paragraphs.map((p) => `<p>${p}</p>`).join(""),
        image: {
            url: unsplashImageUrl(ARTICLE_IMAGE_PHOTOS[input.imageSeed], 1200, 800),
            altText: null,
            width: 1200,
            height: 800,
        },
        publishedAt: input.publishedAt,
        tags: [input.category],
        author: { name: input.author },
        blog,
        relatedProductHandles: input.relatedProductHandles,
        readingMinutes: input.readingMinutes,
    };
}

export const articles: Article[] = articleInputs.map(buildArticle);
